import { Suspense } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const industriesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '픽스코프 산업 분야 | Fixcope Industries',
  url: `${siteUrl}/industries`,
  description: '픽스코프가 서비스하는 산업 분야: 금융, 의료, 제조, 유통, 물류, 교육.',
  inLanguage: 'ko-KR',
};

export default function IndustriesPage() {
  const industries = [
    {
      icon: 'ri-bank-line',
      title: 'Financial Services',
      description: 'Secure, compliant solutions for banking, insurance, and investment firms',
      image: 'https://public.readdy.ai/ai/img_res/6ae64fb50eb0f75c8cb4e9d3688e9cd3.jpg',
      features: [
        'Core Banking Systems',
        'Payment Processing',
        'Risk Management',
        'Regulatory Compliance',
        'Fraud Detection',
        'Digital Banking Platforms'
      ],
      challenges: 'Financial institutions face increasing regulatory requirements, cybersecurity threats, and the need for digital transformation.',
      solutions: 'We provide end-to-end solutions including secure cloud infrastructure, AI-powered fraud detection, and compliant digital banking platforms.'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Healthcare',
      description: 'Digital health solutions that improve patient care and operational efficiency',
      image: 'https://readdy.ai/api/search-image?query=Advanced%20healthcare%20technology%20facility%20with%20modern%20medical%20equipment%2C%20digital%20patient%20monitoring%20systems%2C%20electronic%20health%20records%20on%20large%20displays%2C%20doctors%20using%20tablets%20and%20medical%20software%2C%20clean%20white%20hospital%20environment%20with%20blue%20accent%20lighting%2C%20telemedicine%20setup%2C%20healthcare%20IT%20infrastructure%20with%20patient%20data%20visualization%20and%20medical%20imaging%20technology&width=800&height=600&seq=industry-healthcare-detail-001&orientation=landscape',
      features: [
        'Electronic Health Records',
        'Telemedicine Platforms',
        'Patient Management Systems',
        'Medical Imaging Solutions',
        'Healthcare Analytics',
        'HIPAA Compliance'
      ],
      challenges: 'Healthcare providers need to balance patient care quality with operational efficiency while maintaining strict data privacy standards.',
      solutions: 'Our healthcare solutions include integrated EHR systems, telemedicine platforms, and AI-powered diagnostic tools that enhance patient outcomes.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Manufacturing',
      description: 'Smart factory solutions and IoT integration for Industry 4.0 transformation',
      image: 'https://readdy.ai/api/search-image?query=Smart%20manufacturing%20facility%20with%20automated%20production%20lines%2C%20industrial%20robots%20working%20on%20assembly%2C%20IoT%20sensors%20and%20digital%20monitoring%20systems%2C%20workers%20using%20tablets%20to%20control%20machinery%2C%20modern%20factory%20floor%20with%20blue%20ambient%20lighting%2C%20real-time%20production%20data%20displays%2C%20Industry%204.0%20technology%20integration%20with%20connected%20equipment%20and%20predictive%20maintenance%20systems&width=800&height=600&seq=industry-manufacturing-detail-001&orientation=landscape',
      features: [
        'IoT Integration',
        'Predictive Maintenance',
        'Supply Chain Optimization',
        'Quality Control Systems',
        'Production Analytics',
        'Smart Factory Automation'
      ],
      challenges: 'Manufacturers must optimize production efficiency, reduce downtime, and adapt to rapidly changing market demands.',
      solutions: 'We deliver Industry 4.0 solutions with IoT sensors, predictive analytics, and automated quality control systems for smart manufacturing.'
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'Retail & E-commerce',
      description: 'Omnichannel retail solutions that enhance customer experience and drive sales',
      image: 'https://readdy.ai/api/search-image?query=Modern%20retail%20technology%20environment%20with%20digital%20shopping%20experience%2C%20interactive%20touchscreen%20displays%2C%20mobile%20payment%20systems%2C%20inventory%20management%20tablets%2C%20smart%20retail%20store%20with%20blue%20accent%20lighting%2C%20e-commerce%20fulfillment%20center%20with%20automated%20systems%2C%20customer%20analytics%20dashboards%2C%20omnichannel%20retail%20technology%20integration%20with%20seamless%20shopping%20experience&width=800&height=600&seq=industry-retail-detail-001&orientation=landscape',
      features: [
        'E-commerce Platforms',
        'Inventory Management',
        'Customer Analytics',
        'Point of Sale Systems',
        'Personalization Engines',
        'Omnichannel Integration'
      ],
      challenges: 'Retailers need to provide seamless omnichannel experiences while managing complex inventory and supply chains.',
      solutions: 'Our retail solutions include unified commerce platforms, AI-powered personalization, and real-time inventory management systems.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Logistics & Transportation',
      description: 'Intelligent logistics solutions for optimized supply chain management',
      image: 'https://readdy.ai/api/search-image?query=Advanced%20logistics%20technology%20center%20with%20fleet%20management%20systems%2C%20GPS%20tracking%20displays%2C%20warehouse%20automation%20with%20robotic%20systems%2C%20transportation%20management%20software%20on%20multiple%20screens%2C%20modern%20distribution%20center%20with%20blue%20ambient%20lighting%2C%20real-time%20shipment%20tracking%2C%20supply%20chain%20analytics%20dashboards%2C%20connected%20logistics%20infrastructure%20with%20IoT%20sensors%20and%20route%20optimization%20technology&width=800&height=600&seq=industry-logistics-detail-001&orientation=landscape',
      features: [
        'Fleet Management',
        'Route Optimization',
        'Warehouse Automation',
        'Real-time Tracking',
        'Supply Chain Visibility',
        'Delivery Management'
      ],
      challenges: 'Logistics companies face pressure to reduce costs, improve delivery times, and provide real-time visibility.',
      solutions: 'We offer comprehensive logistics solutions with AI-powered route optimization, automated warehousing, and end-to-end tracking systems.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Education',
      description: 'Digital learning platforms that transform educational experiences',
      image: 'https://readdy.ai/api/search-image?query=Modern%20educational%20technology%20environment%20with%20digital%20learning%20platforms%2C%20students%20using%20tablets%20and%20interactive%20displays%2C%20virtual%20classroom%20setup%20with%20video%20conferencing%2C%20learning%20management%20system%20dashboards%2C%20contemporary%20education%20space%20with%20blue%20accent%20lighting%2C%20e-learning%20infrastructure%20with%20collaborative%20tools%2C%20educational%20analytics%20and%20student%20progress%20tracking%20systems&width=800&height=600&seq=industry-education-detail-001&orientation=landscape',
      features: [
        'Learning Management Systems',
        'Virtual Classrooms',
        'Student Analytics',
        'Content Management',
        'Assessment Tools',
        'Collaboration Platforms'
      ],
      challenges: 'Educational institutions need to provide engaging digital learning experiences while managing diverse student needs.',
      solutions: 'Our education solutions include comprehensive LMS platforms, virtual classroom tools, and AI-powered personalized learning systems.'
    }
  ];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <SEOHead
          title="픽스코프 산업 분야 | Fixcope - 금융·의료·제조·물류·교육"
          description="픽스코프가 서비스하는 다양한 산업 분야를 소개합니다. 금융, 의료, 제조, 유통, 물류, 교육 분야에 특화된 기술 솔루션을 제공합니다."
          keywords="픽스코프 산업, 금융 IT, 의료 IT, 제조 솔루션, 물류 기술, 교육 플랫폼, Fixcope industries"
          canonical="/industries"
          ogTitle="픽스코프 산업 분야 | Fixcope"
          ogDescription="금융·의료·제조·물류·교육 분야에 특화된 픽스코프의 기술 솔루션."
          lang="ko"
          jsonLd={industriesJsonLd}
        />
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-8 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-[#1A2332] mb-6">
              Industries We Serve
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Delivering specialized technology solutions across diverse sectors. Our industry expertise ensures tailored approaches that address unique challenges and drive measurable results.
            </p>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 px-8">
          <div className="max-w-7xl mx-auto space-y-24">
            {industries.map((industry, index) => (
              <div
                key={industry.title}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-12 items-center`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-3xl overflow-hidden shadow-xl h-[400px]">
                    <img
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                    <i className={`${industry.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h2 className="text-4xl font-bold text-[#1A2332] mb-4">
                    {industry.title}
                  </h2>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {industry.description}
                  </p>

                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-[#1A2332] mb-3">
                      Key Challenges
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {industry.challenges}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-[#1A2332] mb-3">
                      Our Solutions
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {industry.solutions}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-[#1A2332] mb-4">
                      Core Capabilities
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {industry.features.map((feature) => (
                        <div
                          key={feature}
                          className="flex items-center space-x-2"
                        >
                          <i className="ri-check-line text-blue-600 text-lg"></i>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button className="mt-8 px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all whitespace-nowrap">
                    Explore Solutions
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-8 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6">
              Ready to Transform Your Industry?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Let's discuss how our industry-specific solutions can help you achieve your business goals.
            </p>
            <button className="px-10 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all whitespace-nowrap">
              Schedule a Consultation
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
